import { array, fn } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { scheduleOnce } from '@ember/runloop';

import { PopularLazyLoadSelector } from 'webapp/utils/common';

import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer from 'webapp/modifiers/visible-check-container';
import willDestroy from 'webapp/modifiers/will-destroy';
import type { Args } from 'webapp/components/boards/select/base';
import BoardsSelectBaseComponent, { FilterSortType } from 'webapp/components/boards/select/base';
import BoardsSelectTableHeaderSorterComponent from 'webapp/components/boards/select/table-header-sorter';
import BoardsSelectTableRowComponent from 'webapp/components/boards/select/table-row';
import CommonListLazyLoadComponent from 'webapp/components/common/list/lazy-load';
import CommonListLoaderComponent from 'webapp/components/common/list/loader';
import FilterLineComponent from 'webapp/components/filter/line';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';

import type Board from 'webapp/models-custom/board';
import type BoardFromAnotherSpace from 'webapp/models-custom/board-from-another-space';
import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSortBoardAtSelectPopupInList from 'webapp/models-custom/filter-sort/board-at-select-popup-in-list';
import FilterSortBoardFromAnotherSpaceAtSelectPopupInList from 'webapp/models-custom/filter-sort/board-from-another-space-at-select-popup-in-list';
import FilterSortReportAtSelectPopupInList from 'webapp/models-custom/filter-sort/report-at-select-popup-in-list';
import type Report from 'webapp/models-custom/report';

export default class BoardsSelectTableComponent extends BoardsSelectBaseComponent<Args> {
	override filterSort:
		| FilterSortBoardAtSelectPopupInList
		| FilterSortBoardFromAnotherSpaceAtSelectPopupInList
		| FilterSortReportAtSelectPopupInList;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const filterSortOptions = {
			selectors: PopularLazyLoadSelector.table,
			onOpenView: (viewId: string) => {
				this.args.onOpenView(viewId);
			},
			getAllItems: () => {
				return this.args.model;
			},
			afterFilter: () => {
				this.triggerContentChange();
			},
		};

		const filterType = this.args.filterSortType;
		if (filterType === FilterSortType.BOARD_FROM_ANOTHER_SPACE) {
			this.filterSort = new FilterSortBoardFromAnotherSpaceAtSelectPopupInList(
				filterSortOptions as FilterSortSettings<BoardFromAnotherSpace>,
			);
		} else if (filterType === FilterSortType.REPORT) {
			this.filterSort = new FilterSortReportAtSelectPopupInList(
				filterSortOptions as FilterSortSettings<Report>,
			);
		} else {
			this.filterSort = new FilterSortBoardAtSelectPopupInList(
				filterSortOptions as FilterSortSettings<Board>,
			);
		}
	}

	@action
	override setupView(node: HTMLElement): void {
		super.setupView(node);
	}

	@action
	override triggerContentChange(): void {
		scheduleOnce('afterRender', this, this.args.onReposition);

		// for visible-check
		const node = this.visibleCheckContainerNode;
		if (node) {
			super.triggerContentChange(node);
		}
	}
	<template>
		<FilterLineComponent
			@model={{this.filterSort}}
			@showJoinedFilterSwitchers={{this.showJoinedFilterSwitchers}}
		/>

		<CommonListLazyLoadComponent
			@classNames={{joinClasses
				(array "lazy-grid" "content" (unless this.filterSort.results "content--no-results"))
			}}
			@filterSort={{this.filterSort}}
			@initialRowCount={{25}}
			@loadMoreRowCount={{25}}
			@animationProperty="id"
			@useScrollableContainer={{true}}
			@autoLoaded={{true}}
			@bottomDistance={{350}}
			as |visibleItems exporter|
		>
			{{! Outside the wrapper, which the popup hides while nothing matches. }}
			<FilterNoResultsComponent @model={{this.filterSort}} />

			<div class="content-wrapper" {{willDestroy this.teardownView}}>
				{{! template-lint-disable table-groups }}
				<table
					class="flexible-table has-checkbox-and-avatar bigtable"
					{{visibleCheckContainer}}
					{{didInsert this.setupView}}
					{{didUpdate this.triggerContentChange visibleItems}}
				>
					<BoardsSelectTableHeaderSorterComponent
						@filterSort={{this.filterSort}}
						@isTableView={{true}}
						@isSelectedAll={{this.isSelectedAll}}
						@checkIfAllItemsCanBeSelected={{@checkIfAllItemsCanBeSelected}}
						@onSelectAll={{fn this.selectAll this.filterSort.results}}
						@hideDatasourceColumn={{this.hideDatasourceColumn}}
					/>
					<tbody>
						{{#each visibleItems as |item|}}
							<BoardsSelectTableRowComponent
								@model={{item}}
								@selected={{@selected}}
								@checkIfItemCanBeSelected={{@checkIfItemCanBeSelected}}
								@onSelect={{@onSelect}}
								@hideDatasourceColumn={{this.hideDatasourceColumn}}
							/>
						{{/each}}
					</tbody>
				</table>

				{{#unless exporter.areAllItemsLoaded}}
					<CommonListLoaderComponent />
				{{/unless}}
			</div>
		</CommonListLazyLoadComponent>
	</template>
}
